import {
  DesignElement,
  LinkedElement,
  isDesignElementFMEARisk,
  isDesignElementISORisk,
  isDesignElementRequirement,
  isDesignElementRisk,
  isDesignElementTestCase,
} from "@design-controls/types";
import { TFunction } from "i18next";

export type CompareSectionId =
  | "description"
  | "properties"
  | "linkedRequirements"
  | "linkedTestCases"
  | "linkedRisks"
  | "attachments";

export type CompareSection = {
  id: CompareSectionId;
  titleKey: string;
};

// Design order. Sections that are empty on both sides are dropped.
export const COMPARE_SECTIONS: readonly CompareSection[] = [
  { id: "description", titleKey: "common.description" },
  { id: "properties", titleKey: "common.properties" },
  {
    id: "linkedRequirements",
    titleKey: "designElementCompare.sections.linkedRequirements",
  },
  {
    id: "linkedTestCases",
    titleKey: "designElementCompare.sections.linkedTestCases",
  },
  { id: "linkedRisks", titleKey: "designElementCompare.sections.linkedRisks" },
  { id: "attachments", titleKey: "common.attachments" },
];

export type PropertyRow = {
  labelKey: string;
  value: string | null | undefined;
};

const formatDate = (value?: string): string | undefined =>
  value ? new Date(value).toLocaleDateString() : undefined;

/**
 * The Properties section for an element, in display order. Both versions go
 * through this same function, so a label can never be paired with the wrong
 * value. Rows with an empty value are dropped by the caller.
 */
export const propertiesOf = (
  element: DesignElement,
  t: TFunction,
): PropertyRow[] => {
  const category = {
    labelKey: "common.category",
    value: element.category?.label,
  };

  if (isDesignElementFMEARisk(element)) {
    const { preMitigation: pre, postMitigation: post } = element;
    return [
      category,
      {
        labelKey: "risks.fmea.overview.fields.failureMode",
        value: element.failureMode,
      },
      {
        labelKey: "risks.fmea.overview.fields.effectsOfFailure",
        value: element.failureModeEffects,
      },
      {
        labelKey: "risks.fmea.overview.fields.causesOfFailure",
        value: element.failureCauses,
      },
      {
        labelKey: "risks.fmea.overview.fields.currentControlsEvaluation",
        value: element.evaluationMethod,
      },
      {
        labelKey: "diffView.fields.initialSeverityAssessment",
        value: pre?.severity,
      },
      {
        labelKey: "diffView.fields.initialOccurrenceAssessment",
        value: pre?.occurrence,
      },
      {
        labelKey: "diffView.fields.initialDetectabilityAssessment",
        value: pre?.detectability,
      },
      {
        labelKey: "diffView.fields.riskControlDescription",
        value: element.riskControl?.mitigation,
      },
      {
        labelKey: "diffView.fields.finalSeverityAssessment",
        value: post?.severity,
      },
      {
        labelKey: "diffView.fields.finalOccurrenceAssessment",
        value: post?.occurrence,
      },
      {
        labelKey: "diffView.fields.finalDetectabilityAssessment",
        value: post?.detectability,
      },
    ];
  }

  if (isDesignElementISORisk(element)) {
    const { preMitigation: pre, postMitigation: post } = element;
    return [
      category,
      {
        labelKey: "risks.iso.overview.fields.hazard",
        value: element.hazard,
      },
      {
        labelKey: "risks.iso.overview.fields.reasonableForeseeableUseMisuse",
        value: element.foreseeableUseMisuse,
      },
      {
        labelKey: "risks.iso.overview.fields.hazardSituation",
        value: element.hazardSituation,
      },
      { labelKey: "risks.iso.overview.fields.harm", value: element.harm },
      {
        labelKey: "diffView.fields.initialProbabilityAssessment",
        value: pre?.probability,
      },
      {
        labelKey: "diffView.fields.initialSeverityAssessment",
        value: pre?.severity,
      },
      {
        labelKey: "designElementCompare.fields.initialRiskLevel",
        value: pre?.riskLevel,
      },
      {
        labelKey: "diffView.fields.riskControlDescription",
        value: element.riskControl?.mitigation,
      },
      {
        labelKey: "diffView.fields.finalProbabilityAssessment",
        value: post?.probability,
      },
      {
        labelKey: "diffView.fields.finalSeverityAssessment",
        value: post?.severity,
      },
      {
        labelKey: "designElementCompare.fields.finalRiskLevel",
        value: post?.riskLevel,
      },
    ];
  }

  if (isDesignElementRisk(element)) {
    return [category];
  }

  const component = {
    labelKey: "common.component",
    value: element.component?.label,
  };

  if (isDesignElementTestCase(element)) {
    const result = element.testResult;
    return [
      category,
      component,
      {
        labelKey: "diffView.fields.result",
        value: result?.result && t(`statuses.testResult.${result.result}`),
      },
      { labelKey: "diffView.fields.tester", value: result?.executedBy },
      {
        labelKey: "diffView.fields.executedDated",
        value: formatDate(result?.date),
      },
      { labelKey: "diffView.fields.notes", value: result?.notes },
    ];
  }

  return [category, component];
};

const linkedElementsOf = (element: DesignElement): LinkedElement[] => {
  if (isDesignElementRisk(element)) {
    return [...element.sourceItems, ...element.mitigationItems];
  }
  return [
    ...element.parent,
    ...element.children,
    ...element.sourcedItems,
    ...element.mitigates,
  ];
};

// Sorted by code so a reordered API response doesn't show up as a change.
export const linkedElementsFor = (
  element: DesignElement,
  section: "linkedRequirements" | "linkedTestCases" | "linkedRisks",
): LinkedElement[] => {
  const matches = {
    linkedRequirements: isDesignElementRequirement,
    linkedTestCases: isDesignElementTestCase,
    linkedRisks: isDesignElementRisk,
  }[section];
  const byId = new Map(
    linkedElementsOf(element)
      .filter((linked) => matches(linked))
      .map((linked) => [linked.id, linked]),
  );
  return [...byId.values()].sort((a, b) =>
    a.code.localeCompare(b.code, undefined, { numeric: true }),
  );
};
